import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import LoadingOverlay from "react-loading-overlay";
import { Link, useParams } from "react-router-dom";

import config from "config/config";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { usePolicyDetailLinks } from "js/placeholderData/policyDetail";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import {
  getName,
  getPolicyType,
  phoneNumberFormatting,
  stringFormatingToPascal,
} from "js/formatting";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";
import { getFinancials } from "service/policy/getFinancials";
import { useTranslation } from "react-i18next";
import {
  getAgentsFromPolicy,
  isPolicyPayoutAnnuity,
  isFlagEnabled,
  isPolicyInactiveByPolicyStatus,
} from "js/utilities";

import CardWithFields from "components/General/CardWithFields";
import DepositsAndWithdrawals from "components/General/DepositsAndWithdrawals";
import UserNotificationDialogOnLogin from "components/Profile/Dialogs/UserNotificationDialogOnLogin";
import SimpleDataRow from "components/General/SimpleDataRow";
import Carousel from "components/General/Carousel";
import DeathBenefitCard from "components/Policy/AnnuityDetail/DeathBenefitCard";
import PaymentSummaryCard from "components/Policy/Detail/PaymentSummary";
import ThingsYouCanDoCard from "components/Policy/Detail/ThingsYouCanDoCard";
import ContractValues from "components/Policy/AnnuityDetail/ContractValues";
import PaymentDetails from "components/Policy/AnnuityDetail/PaymentDetails";
import CoverageAndDocuments from "components/Policy/AnnuityDetail/CoverageAndDocuments";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import BeneficiariesCard from "components/Policy/Detail/BeneficiariesCard";

export default function PolicyDetails() {
  const { t } = useTranslation();
  const links = usePolicyDetailLinks(true);
  LoadingOverlay.propTypes = undefined;
  const { policies, claims, isLoading } = useSelector((state) => state.data);
  const { user } = useSelector((state) => state.user);
  const { policy_id } = useParams();
  const [firstName, setFirstName] = useState();
  const [openUserNotificationDialog, setOpenUserNotificationDialog] =
    useState(false);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [deathBenefitValue, setDeathBenefitValue] = useState();
  const [agentData, setAgentData] = useState([]);
  const [loading, setLoading] = useState(isLoading);
  const viewServiceandClaim = useAuthorizationCheck(4);
  const viewBeneficiaries = useAuthorizationCheck(9);
  const viewPaymentSummary = useAuthorizationCheck(10);
  const viewAgent = useAuthorizationCheck(11);
  const dispatch = useDispatch();

  const policy = policies.find((x) => x.contractID === policy_id);

  useEffect(() => {
    if (
      isFlagEnabled("E_DELIVERY_ENABLE") &&
      user.notificationPreference === 0
    ) {
      setOpenUserNotificationDialog(true);
    }
    let party = policy?.parties?.find(
      (x) => x.person?.taxId === user.identityNumber
    );
    setFirstName(stringFormatingToPascal(party?.person?.firstName));
  }, [policy, user]);

  // setup breadcrumb for this page
  useBreadcrumb([
    { name: t("CustomerPolicyDetailsBreadcrumb-home"), url: "/policies" },
    {
      name: t("CustomerPolicyDetailsBreadcrumb-annuity-contract-details"),
      url: "#",
    },
  ]);

  useEffect(() => {
    if (viewAgent) {
      const agent = getAgentsFromPolicy(policy);
      setAgentData(agent);
    }
    setLoading(true);
    if (filteredDocuments?.length === 0) {
      dispatch(
        getCorrespondenceList({
          policyId: [policy?.contractID],
          agentId: "",
          beginDate: moment()
            .subtract(parseInt(config.PAYMENT_STATEMENT_DURATION), "year")
            .format("MM/DD/YYYY"),
          endDate: moment().format("MM/DD/YYYY"),
        })
      ).then((response) => {
        if (response?.payload) {
          if (response?.meta?.requestStatus === "fulfilled") {
            setFilteredDocuments(
              response.payload.allAlerts.filter(
                (x) => x.document.typeOfDocument.toUpperCase() === "TAX"
              )
            );
          }
        }
      });
    }
    getFinancials(policy)
      .then((response) => {
        const deathBenefit = response?.cashValues[0]?.deathBenefit;
        setDeathBenefitValue(deathBenefit);
        setLoading(false);
      })
      .catch((error) => {
        console.error("getFinancials failing", error);
        setLoading(false);
      });
  }, [policies]);

  return (
    <>
      <EverlakeLoadingOverlay
        isLoading={loading}
        loadingText={t("CustomerPolicyDetails-loadingTex")}
      >
        <>
          {policies && policies.length > 0 ? (
            <>
              <UserNotificationDialogOnLogin
                showDialog={openUserNotificationDialog}
                setShowDialog={setOpenUserNotificationDialog}
              />
              <div className="container mb-8">
                <h1>
                  {t("CustomerPolicyDetails-annuity-contract-details-title")}
                </h1>
                <p>{t("CustomerPolicyDetails-view-annuity-details")}</p>
                <hr />
              </div>

              <Carousel policies={policies} />

              <div className="container ui-component-spacing">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-2/3 lg:pr-3">
                    <CoverageAndDocuments
                      policy={policy}
                      filteredDocuments={filteredDocuments}
                    />

                    {policy && !isPolicyPayoutAnnuity(policy) && (
                      <ContractValues contractID={policy.contractID} />
                    )}

                    {policy?.streams?.length > 0 && (
                      <PaymentDetails paymentStream={policy.streams} />
                    )}

                    {isPolicyPayoutAnnuity(policy) ? (
                      <>
                        {viewBeneficiaries &&
                          policy?.annuityPayoutIndicatorFlag !== "Y" && (
                            <BeneficiariesCard data={policy} />
                          )}
                      </>
                    ) : (
                      <>
                        {viewBeneficiaries && policy && (
                          <DeathBenefitCard
                            data={policy}
                            deathBenefit={deathBenefitValue}
                          />
                        )}
                      </>
                    )}

                    {policy && !policy?.streams && (
                      <DepositsAndWithdrawals
                        deposits={
                          policy?.cashValue?.grossTransactions?.deposits
                        }
                        withdrawals={
                          policy?.cashValue?.grossTransactions?.withdrawals
                        }
                      />
                    )}
                    {agentData?.length > 0 &&
                      agentData?.map((agent, idx) => {
                        const { person, address, phone, email } =
                          agent?.parties[0];
                        return (
                          <CardWithFields
                            key={idx}
                            ariaLabel={t(
                              "CustomerPolicyDetails-annuity-agent-aria-label"
                            )}
                            cardTitle={t(
                              "CustomerPolicyDetails-annuity-agent-card-title"
                            )}
                            cardFields={[
                              {
                                title: t(
                                  "CustomerPolicyDetails-annuity-agent-fields-name"
                                ),
                                value: getName(person),
                              },
                              {
                                title: t(
                                  "CustomerPolicyDetails-annuity-agent-fields-address"
                                ),
                                value: (
                                  <>
                                    {address?.addressLine1}
                                    <br />
                                    {address?.addressLine2 ?? ""}
                                    {address?.addressLine2 ? <br /> : ""}
                                    {`${address?.city}, ${
                                      address?.state
                                    } ${address?.postalCode.substring(0, 5)}`}
                                  </>
                                ),
                              },
                              {
                                title: t(
                                  "CustomerPolicyDetails-annuity-agent-fields-phone"
                                ),
                                value: (
                                  <a href={`tel:+1${phone.phoneNumber}`}>
                                    {phoneNumberFormatting(phone.phoneNumber)}
                                  </a>
                                ),
                              },
                              {
                                title: t(
                                  "CustomerPolicyDetails-annuity-agent-fields-email"
                                ),
                                value: (
                                  <a href={`mailto:+1${email.address}`}>
                                    {email.address}
                                  </a>
                                ),
                              },
                            ]}
                          />
                        );
                      })}
                  </div>

                  <div className="lg:w-1/3 lg:pl-3">
                    <ThingsYouCanDoCard data={links} policy={policy} />

                    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6 lg:grid-cols-1 lg:gap-0">
                      {viewServiceandClaim &&
                        claims &&
                        claims.filter((p) => p.contractID === policy_id)
                          .length > 0 && (
                          <div className="mb-4 ui-card md:mb-6">
                            <div className="px-4 pt-4 pb-1 border-b border-theme-rule-light">
                              <h2 className="text-sm font-semibold font-body">
                                {t(
                                  "CustomerPolicyDetails-annuity-claims-title"
                                )}
                              </h2>
                            </div>
                            <div>
                              {claims
                                .filter((p) => p.contractID === policy_id)
                                .map((claim, idx) => (
                                  <SimpleDataRow
                                    type="customerServicesAndClaims"
                                    key={`Customer claim - ${idx}`}
                                    data={claim}
                                  />
                                ))}
                            </div>
                            <Link
                              to="/services-claims/claims"
                              className="text-center ui-card-btn-more"
                              aria-label="View more claims"
                            >
                              {t(
                                "CustomerPolicyDetails-annuity-claims-view-more"
                              )}
                            </Link>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="container mb-8">
                <p> {t("CustomerPolicyDetails-annuity-no-policy-display")}</p>
              </div>
            </>
          )}
        </>
      </EverlakeLoadingOverlay>
    </>
  );
}
